// ═══════════════════════════════════════════════════
//  ReconAI Pro v3 – Reconciliation Matching Engine (v2)
//  Tuned for real Indian ledger reconciliation:
//   - Invoice ref cross-matching (company Inv#FI109 ↔ supplier FI 111/25-26)
//   - Bill ref cross-matching (company RBD25/11 in narration ↔ supplier Bill no # RBD25/11)
//   - Cheque ref matching (company CH#719570 ↔ supplier Receipt with same cheque)
//   - DN/CN cross-matching (company Debit Note #65 ↔ supplier Credit Note 65)
//   - Polarity inversion (company creditor CR purchase ↔ supplier debtor DR sale)
//   - Amount tolerance for rounding (₹0.20 round-off, paise differences)
//   - 1:N settlement matching (one BPN payment = multiple sub-items)
//   - Date tolerance up to 60 days (Tally posting vs actual date)
// ═══════════════════════════════════════════════════

import { docNoSimilarity, stringSimilarity, daysBetween, normalizeNarration, levenshtein, extractVoucherNumber, extractCheckNumber, extractInvFromNarration } from '../utils/helpers';

export const DEFAULT_TOLERANCES = {
  dateDays: 60,           // Tally posting dates can differ significantly
  amountAbsolute: 5.00,   // Allow ₹5 for rounding
  amountPercent: 0.5,
  narrationThreshold: 0.40,
  materialityThreshold: 50,
  groupMaxSize: 10,
  groupDateWindow: 90,
};

const WEIGHTS = {
  invoiceRef: 0.30,   // Highest weight: cross-ledger invoice/bill ref
  amount: 0.25,
  docNo: 0.15,
  date: 0.10,
  narration: 0.10,
  txType: 0.10,
};

// ── Type Compatibility ──────────────────
const TYPE_PAIRS = {
  'Purchase': ['Sales','Purchase','Expense Voucher','Sales Return'],
  'Sales': ['Purchase','Sales','Expense Voucher'],
  'Sales Return': ['Purchase','Sales Return','Debit Note','Credit Note'],
  'Payment': ['Receipt','Payment'],
  'Receipt': ['Payment','Receipt'],
  'Debit Note': ['Credit Note','Debit Note','Excess/Shortage','Sales Return'],
  'Credit Note': ['Debit Note','Credit Note','Excess/Shortage'],
  'Excess/Shortage': ['Debit Note','Credit Note','Excess/Shortage'],
  'Jobwork': ['Jobwork','Purchase','Sales','Expense Voucher'],
  'Expense Voucher': ['Sales','Purchase','Expense Voucher'],
  'Journal': ['Journal','Payment','Receipt'],
  'TDS': ['TDS','Journal'],
};

const typeCompat = (t1,t2) => {
  if(t1===t2) return 1.0;
  if((TYPE_PAIRS[t1]||[]).includes(t2)) return 0.8;
  return 0.15;
};

const absAmount = (tx) => {
  const s = (tx.debit||0) - (tx.credit||0);
  return Math.abs(s) || ((tx.debit||0) + (tx.credit||0));
};

// ── Invoice Reference Matching ──────────
// This is THE most important matching signal for Indian ledgers.
// Company narration: "Inv#FI109/25-26" ↔ Supplier docNo: "FI111/25-26" or "FI 109/25-26"
// Company narration: "RBD25/11" ↔ Supplier narration: "Bill no # RBD25/11"
const invoiceRefScore = (compTx, suppTx) => {
  // 1. Direct invoice ref match (FI xxx/25-26)
  const cInv = (compTx.invoiceRef||'').replace(/\s/g,'').toUpperCase();
  const sInv = (suppTx.invoiceRef||'').replace(/\s/g,'').toUpperCase();
  const sDoc = (suppTx.docNo||'').replace(/\s/g,'').toUpperCase();
  const cDoc = (compTx.docNo||'').replace(/\s/g,'').toUpperCase();

  if (cInv && sDoc && (cInv === sDoc || sDoc.includes(cInv) || cInv.includes(sDoc))) return 1.0;
  if (cInv && sInv && (cInv === sInv || sInv.includes(cInv) || cInv.includes(sInv))) return 1.0;
  if (sInv && cDoc && (sInv === cDoc || cDoc.includes(sInv) || sInv.includes(cDoc))) return 1.0;

  // 2. Bill ref match (RBD25/11)
  const cBill = (compTx.billRef||'').toUpperCase();
  const sBill = (suppTx.billRef||'').toUpperCase();
  if (cBill && sBill && cBill === sBill) return 1.0;
  // Check if company narration mentions supplier's bill ref or vice versa
  if (sBill && compTx.narration && compTx.narration.toUpperCase().includes(sBill)) return 0.95;
  if (cBill && suppTx.narration && suppTx.narration.toUpperCase().includes(cBill)) return 0.95;

  // 3. Cheque ref match (including extraction from narration for BPN grouped entries)
  const cChq = (compTx.chequeRef||'').toUpperCase() || extractCheckNumber((compTx.narration||'')).toUpperCase();
  const sChq = (suppTx.chequeRef||'').toUpperCase() || extractCheckNumber((suppTx.narration||'')).toUpperCase();
  if (cChq && sChq && cChq === sChq) return 0.90;
  // Check if cheque number appears in other side's narration
  if (cChq && cChq.length >= 4 && suppTx.narration && suppTx.narration.toUpperCase().includes(cChq)) return 0.85;
  if (sChq && sChq.length >= 4 && compTx.narration && compTx.narration.toUpperCase().includes(sChq)) return 0.85;

  // 4. DN/CN ref match
  const cDn = (compTx.dnCnRef||'').toUpperCase();
  const sDn = (suppTx.dnCnRef||'').toUpperCase();
  if (cDn && sDn && (cDn === sDn || cDn.includes(sDn) || sDn.includes(cDn))) return 0.90;

  // 5. Voucher number matching (nnn/YYYY-YYYY pattern)
  // Company "76/2025-2026" ↔ Supplier "76/2025-2026" — MUST match full year pattern to avoid cross-FY false positives
  // First check full voucher ref match (e.g., "76/2025-2026" in both)
  if (cDoc.length > 5 && sDoc.length > 5) {
    const cVR = cDoc.match(/\d+\/\d{4}-\d{4}/);
    const sVR = sDoc.match(/\d+\/\d{4}-\d{4}/);
    if (cVR && sVR && cVR[0] === sVR[0]) return 1.0;
  }
  // Only match bare voucher numbers if the financial year also matches or is absent
  const cVouchNum = extractVoucherNumber(cDoc) || extractVoucherNumber(cInv);
  const sVouchNum = extractVoucherNumber(sDoc) || extractVoucherNumber(sInv);
  if (cVouchNum && sVouchNum && cVouchNum === sVouchNum) {
    // Verify financial years don't conflict
    const cFY = (cDoc + ' ' + cInv).match(/\/(\d{4}-\d{4})/);
    const sFY = (sDoc + ' ' + sInv).match(/\/(\d{4}-\d{4})/);
    if (cFY && sFY && cFY[1] !== sFY[1]) { /* cross-FY mismatch, skip */ }
    else return 0.95;
  }

  // 6. Check if document number appears in the OTHER side's narration
  const cNarrClean = (compTx.narration||'').replace(/\s/g,'').toUpperCase();
  const sNarrClean = (suppTx.narration||'').replace(/\s/g,'').toUpperCase();

  if (sDoc.length > 3 && cNarrClean.includes(sDoc)) {
    if (cDoc.includes('SYSTEM')) return 1.0;
    return 0.90;
  }
  if (cDoc.length > 3 && sNarrClean.includes(cDoc)) {
    if (sDoc.includes('SYSTEM')) return 1.0;
    return 0.90;
  }

  // 7. Voucher number in narration cross-check
  if (cVouchNum && sNarrClean.includes(cVouchNum)) return 0.80;
  if (sVouchNum && cNarrClean.includes(sVouchNum)) return 0.80;

  // 8. Company Purchase narration contains supplier's Sales invoice number
  // Company: "Inv#2517196 Dated on 2025-12-19 Bill 123860" ↔ Supplier Vch No: 2517196
  // Also: BPN grouped entry: "CH#669172 Inv#2422533 Dated on 2025-01-30 Bill Amt 61..."
  const cInvRefs = extractInvFromNarration(compTx.narration || '');
  const sInvRefs = extractInvFromNarration(suppTx.narration || '');

  // Company narration Inv# → Supplier docNo (purchase → sales matching)
  if (cInvRefs.length > 0 && sDoc.length >= 3) {
    for (const ref of cInvRefs) {
      const refUp = ref.toUpperCase();
      // Exact match: Inv#2517196 = Vch No 2517196
      if (refUp === sDoc) return 1.0;
      // Partial/contains match
      if (sDoc.includes(refUp) || refUp.includes(sDoc)) return 0.95;
    }
  }
  // Supplier narration Inv# → Company docNo (reverse)
  if (sInvRefs.length > 0 && cDoc.length >= 3) {
    for (const ref of sInvRefs) {
      const refUp = ref.toUpperCase();
      if (refUp === cDoc) return 1.0;
      if (cDoc.includes(refUp) || refUp.includes(cDoc)) return 0.95;
    }
  }

  // 9. Broader narration Inv# cross-check against ANY reference on the other side
  // Company Inv#2517196 appears anywhere in supplier narration (or vice versa)
  if (cInvRefs.length > 0) {
    for (const ref of cInvRefs) {
      if (ref.length >= 4 && sNarrClean.includes(ref.toUpperCase())) return 0.85;
    }
  }
  if (sInvRefs.length > 0) {
    for (const ref of sInvRefs) {
      if (ref.length >= 4 && cNarrClean.includes(ref.toUpperCase())) return 0.85;
    }
  }

  return 0
};

// ── Compute Match Score ─────────────────
export const computeMatchScore = (compTx, suppTx, tol = DEFAULT_TOLERANCES) => {
  const scores = {};

  // Invoice/Bill/Cheque reference (most important)
  scores.invoiceRef = invoiceRefScore(compTx, suppTx);

  // Amount match (with polarity inversion: creditor CR = debtor DR)
  const cAmt = absAmount(compTx), sAmt = absAmount(suppTx);
  const amtDiff = Math.abs(cAmt - sAmt);
  const amtPct = cAmt > 0 ? (amtDiff/cAmt)*100 : 100;
  if (amtDiff <= tol.amountAbsolute) scores.amount = 1.0;
  else if (amtPct <= tol.amountPercent) scores.amount = 0.95;
  else if (amtDiff <= tol.amountAbsolute * 20) scores.amount = Math.max(0, 1 - amtDiff/(tol.amountAbsolute*20));
  else scores.amount = Math.max(0, 1 - amtPct/10);

  // Document number similarity
  scores.docNo = docNoSimilarity(compTx.docNo, suppTx.docNo);

  // Date closeness (wide tolerance for Indian ledgers)
  const dd = daysBetween(compTx.date, suppTx.date);
  if (dd === 0) scores.date = 1.0;
  else if (dd <= 7) scores.date = 0.9;
  else if (dd <= tol.dateDays) scores.date = Math.max(0.3, 0.9 - (dd/tol.dateDays)*0.6);
  else scores.date = 0;

  // Narration similarity
  scores.narration = stringSimilarity(normalizeNarration(compTx.narration), normalizeNarration(suppTx.narration));

  // Transaction type compatibility
  scores.txType = typeCompat(compTx.type, suppTx.type);

  const composite = Object.keys(WEIGHTS).reduce((s,k) => s + (scores[k]||0)*WEIGHTS[k], 0);

  return {
    composite: Math.round(composite*100)/100,
    scores, weights: WEIGHTS,
    amountDifference: Math.round(amtDiff*100)/100,
    dateDifference: dd,
  };
};

const getMatchRule = (score) => {
  if (score.scores.invoiceRef >= 0.9 && score.scores.amount >= 0.9) return 'Invoice Ref + Amount';
  if (score.scores.invoiceRef >= 0.85) return 'Cheque/Bill Ref Match';
  if (score.scores.docNo >= 0.9 && score.scores.amount >= 0.9) return 'Exact Doc + Amount';
  if (score.scores.docNo >= 0.7 && score.scores.date >= 0.5 && score.scores.amount >= 0.9) return 'Doc + Date + Amount';
  if (score.scores.narration >= 0.5 && score.scores.amount >= 0.9) return 'Narration + Amount';
  if (score.scores.amount >= 0.95 && score.scores.date >= 0.5) return 'Amount + Date';
  if (score.composite >= 0.55) return 'Fuzzy Composite';
  return 'Low Confidence';
};

// ═════ MAIN MATCHING ENGINE ═════
export const runMatching = (compTxnsRaw, suppTxnsRaw, tolerances = DEFAULT_TOLERANCES, groupByDoc = false) => {
  const matchGroups = [];
  const mComp = new Set(), mSupp = new Set();

  const compress = (txns) => {
    const groups = {};
    const ungrouped = [];
    txns.forEach(t => {
      const doc = (t.docNo||'').trim().toUpperCase();
      if(!doc || doc.length < 3) { ungrouped.push({...t, _underlyingIds: [t.id]}); return; }
      if(!groups[doc]) groups[doc] = [];
      groups[doc].push(t);
    });
    const out = [...ungrouped];
    Object.entries(groups).forEach(([doc, items]) => {
      if(items.length === 1) {
        out.push({...items[0], _underlyingIds: [items[0].id]});
        return;
      }
      let dr = 0, cr = 0;
      items.forEach(t => { dr += (t.debit||0); cr += (t.credit||0); });
      const net = dr - cr;
      const base = {...items[0]};
      base._underlyingIds = items.map(t => t.id);
      base.isGrouped = true;
      base.id = '_grp_' + items[0].id;
      if (net >= 0) { base.debit = net; base.credit = 0; }
      else { base.debit = 0; base.credit = Math.abs(net); }
      out.push(base);
    });
    return out;
  };

  // ALWAYS auto-compress BPN/multi-line payment entries (even if groupByDoc is off)
  // BPN (Bank Payment Note) entries have multiple debit lines under same doc no → net = total payment
  const autoCompressPayments = (txns) => {
    const multiLineDocPrefixes = /^(BPN|BPS|BR|JW)\d/i;
    const needsCompress = [];
    const passThrough = [];
    txns.forEach(t => {
      if (multiLineDocPrefixes.test(t.docNo || '')) needsCompress.push(t);
      else passThrough.push({...t, _underlyingIds: [t.id]});
    });
    if (needsCompress.length === 0) return passThrough;
    const compressed = compress(needsCompress);
    return [...passThrough, ...compressed];
  };

  // Ensure every transaction has _underlyingIds for the unpack phase
  const ensureUnderlyingIds = (txns) => txns.map(t => t._underlyingIds ? t : { ...t, _underlyingIds: [t.id] });
  const companyTxns = ensureUnderlyingIds(groupByDoc ? compress(compTxnsRaw) : autoCompressPayments(compTxnsRaw));
  const supplierTxns = ensureUnderlyingIds(groupByDoc ? compress(suppTxnsRaw) : autoCompressPayments(suppTxnsRaw));

  // Index supplier by various keys for fast lookup
  const suppByAmt = {};
  supplierTxns.forEach(tx => {
    const amt = Math.round(absAmount(tx));
    for (let b = amt-500; b <= amt+500; b += 100) {
      const k = Math.floor(b/100)*100;
      if (!suppByAmt[k]) suppByAmt[k] = [];
      if (!suppByAmt[k].includes(tx)) suppByAmt[k].push(tx);
    }
  });

  // Index supplier by invoice ref
  const suppByInvRef = {};
  supplierTxns.forEach(tx => {
    const inv = (tx.invoiceRef||'').replace(/\s/g,'').toUpperCase();
    if (inv) { if (!suppByInvRef[inv]) suppByInvRef[inv] = []; suppByInvRef[inv].push(tx); }
    const bill = (tx.billRef||'').toUpperCase();
    if (bill) { if (!suppByInvRef[bill]) suppByInvRef[bill] = []; suppByInvRef[bill].push(tx); }
    const doc = (tx.docNo||'').replace(/\s/g,'').toUpperCase();
    if (doc && doc.length > 2) { if (!suppByInvRef[doc]) suppByInvRef[doc] = []; suppByInvRef[doc].push(tx); }
  });

  // ── Phase 1: High-confidence 1:1 matching ──
  for (const compTx of companyTxns) {
    if (mComp.has(compTx.id)) continue;

    // Build candidate set from multiple indexes
    const candidates = new Map();
    // By invoice ref
    const cInv = (compTx.invoiceRef||'').replace(/\s/g,'').toUpperCase();
    if (cInv && suppByInvRef[cInv]) suppByInvRef[cInv].forEach(s => candidates.set(s.id, s));
    // By bill ref
    const cBill = (compTx.billRef||'').toUpperCase();
    if (cBill && suppByInvRef[cBill]) suppByInvRef[cBill].forEach(s => candidates.set(s.id, s));
    // By cheque ref - search supplier narrations
    const cChq = (compTx.chequeRef||'').replace('CH#','');
    if (cChq) supplierTxns.filter(s => !mSupp.has(s.id) && s.narration && s.narration.includes(cChq)).forEach(s => candidates.set(s.id, s));
    // By amount bucket
    const cAmt = Math.round(absAmount(compTx));
    const bucket = Math.floor(cAmt/100)*100;
    // Check current bucket and adjacent buckets to handle boundary cases (e.g., ₹99.50 vs ₹100.50)
    for (const b of [bucket - 100, bucket, bucket + 100]) {
      if (suppByAmt[b]) suppByAmt[b].forEach(s => candidates.set(s.id, s));
    }
    // By docNo
    const cDoc = (compTx.docNo||'').replace(/\s/g,'').toUpperCase();
    if (cDoc && suppByInvRef[cDoc]) suppByInvRef[cDoc].forEach(s => candidates.set(s.id, s));

    let bestMatch = null, bestScore = null;
    for (const [sid, suppTx] of candidates) {
      if (mSupp.has(sid)) continue;
      const score = computeMatchScore(compTx, suppTx, tolerances);
      if (score.composite >= 0.45 && (!bestScore || score.composite > bestScore.composite)) {
        bestMatch = suppTx; bestScore = score;
      }
    }

    if (bestMatch && bestScore) {
      const rule = getMatchRule(bestScore);
      const status = bestScore.composite >= 0.70 ? 'CONFIRMED' : 'SUGGESTED';
      matchGroups.push({
        id: '_mg' + Math.random().toString(36).substr(2,9),
        matchType: '1:1',
        companyItems: [compTx.id], supplierItems: [bestMatch.id],
        confidence: Math.round(bestScore.composite*100),
        rule, status, scoreDetail: bestScore,
        amountDiff: bestScore.amountDifference,
        dateDiff: bestScore.dateDifference,
        companyTotal: absAmount(compTx),
        supplierTotal: absAmount(bestMatch),
      });
      mComp.add(compTx.id);
      mSupp.add(bestMatch.id);
    }
  }

  // ── Phase 2: 1:N Group matching ──
  // One company payment (BPN) may settle multiple supplier invoices
  const remComp = companyTxns.filter(t => !mComp.has(t.id) && absAmount(t) >= tolerances.materialityThreshold);
  const remSupp = supplierTxns.filter(t => !mSupp.has(t.id));

  for (const compTx of remComp) {
    if (mComp.has(compTx.id)) continue;
    const cAmt = absAmount(compTx);
    if (cAmt < tolerances.materialityThreshold) continue;

    const cands = remSupp.filter(s =>
      !mSupp.has(s.id) &&
      absAmount(s) <= cAmt + tolerances.amountAbsolute &&
      daysBetween(compTx.date, s.date) <= tolerances.groupDateWindow
    ).slice(0, 25);

    if (cands.length < 2) continue;

    // Greedy subset sum
    const sorted = [...cands].sort((a,b) => absAmount(b) - absAmount(a));
    let accum = 0; const group = [];
    for (const s of sorted) {
      if (group.length >= tolerances.groupMaxSize) break;
      const sa = absAmount(s);
      if (accum + sa <= cAmt + tolerances.amountAbsolute) {
        group.push(s); accum += sa;
      }
    }
    const diff = Math.abs(cAmt - accum);
    if (group.length >= 2 && diff <= tolerances.amountAbsolute * 2) {
      // Verify at least some reference/narration correlation to avoid spurious sum-matches
      const cNarr = ((compTx.narration||'') + ' ' + (compTx.chequeRef||'')).toUpperCase();
      const hasRefCorrelation = group.some(s => {
        const sNarr = ((s.narration||'') + ' ' + (s.docNo||'')).toUpperCase();
        // Check if any reference from one side appears in the other
        if (compTx.chequeRef && sNarr.includes(compTx.chequeRef.replace('CH#','').toUpperCase())) return true;
        if (s.invoiceRef && cNarr.includes(s.invoiceRef.toUpperCase())) return true;
        if (s.docNo && s.docNo.length >= 4 && cNarr.includes(s.docNo.toUpperCase())) return true;
        // Check narration word overlap (at least one meaningful word in common)
        const cWords = new Set(normalizeNarration(compTx.narration).split(/\s+/).filter(w => w.length > 3));
        const sWords = normalizeNarration(s.narration).split(/\s+/).filter(w => w.length > 3);
        return sWords.some(w => cWords.has(w));
      });
      // Only group if we have some correlation OR it's a BPN/payment entry (likely multi-invoice settlement)
      const isBPN = /^(BPN|BPS)/i.test(compTx.docNo || '');
      if (hasRefCorrelation || isBPN) {
        matchGroups.push({
          id: '_mg' + Math.random().toString(36).substr(2,9),
          matchType: '1:N',
          companyItems: [compTx.id], supplierItems: group.map(s => s.id),
          confidence: Math.round(Math.max(50, 80 - group.length*2 - diff*2)),
          rule: 'Grouped Settlement', status: 'SUGGESTED',
          scoreDetail: { composite: 0.65, scores: { amount: diff<=0.01?1:0.9, groupSize: group.length } },
          amountDiff: Math.round(diff*100)/100,
          dateDiff: Math.max(...group.map(s => daysBetween(compTx.date, s.date))),
          companyTotal: cAmt,
          supplierTotal: Math.round(accum*100)/100,
        });
        mComp.add(compTx.id);
        group.forEach(s => mSupp.add(s.id));
      }
    }
  }

  // ── Phase 3: N:1 Group matching ──
  // Multiple company items (e.g., PI + JW) settle one supplier invoice
  const remSupp2 = supplierTxns.filter(t => !mSupp.has(t.id) && absAmount(t) >= tolerances.materialityThreshold);
  const remComp2 = companyTxns.filter(t => !mComp.has(t.id));

  for (const suppTx of remSupp2) {
    if (mSupp.has(suppTx.id)) continue;
    const sAmt = absAmount(suppTx);
    const cands = remComp2.filter(c =>
      !mComp.has(c.id) && absAmount(c) <= sAmt + tolerances.amountAbsolute &&
      daysBetween(suppTx.date, c.date) <= tolerances.groupDateWindow
    ).slice(0, 25);

    if (cands.length < 2) continue;
    const sorted = [...cands].sort((a,b) => absAmount(b) - absAmount(a));
    let accum = 0; const group = [];
    for (const c of sorted) {
      if (group.length >= tolerances.groupMaxSize) break;
      const ca = absAmount(c);
      if (accum + ca <= sAmt + tolerances.amountAbsolute) { group.push(c); accum += ca; }
    }
    const diff = Math.abs(sAmt - accum);
    if (group.length >= 2 && diff <= tolerances.amountAbsolute * 2) {
      // Verify at least some reference/narration correlation to avoid spurious sum-matches
      const sNarr = ((suppTx.narration||'') + ' ' + (suppTx.chequeRef||'')).toUpperCase();
      const hasRefCorrelation = group.some(c => {
        const cNarr = ((c.narration||'') + ' ' + (c.docNo||'')).toUpperCase();
        if (suppTx.chequeRef && cNarr.includes(suppTx.chequeRef.replace('CH#','').toUpperCase())) return true;
        if (c.invoiceRef && sNarr.includes(c.invoiceRef.toUpperCase())) return true;
        if (c.docNo && c.docNo.length >= 4 && sNarr.includes(c.docNo.toUpperCase())) return true;
        const sWords = new Set(normalizeNarration(suppTx.narration).split(/\s+/).filter(w => w.length > 3));
        const cWords = normalizeNarration(c.narration).split(/\s+/).filter(w => w.length > 3);
        return cWords.some(w => sWords.has(w));
      });
      const isBPN = /^(BPN|BPS)/i.test(suppTx.docNo || '');
      if (hasRefCorrelation || isBPN) {
        matchGroups.push({
          id: '_mg' + Math.random().toString(36).substr(2,9),
          matchType: 'N:1',
          companyItems: group.map(c => c.id), supplierItems: [suppTx.id],
          confidence: Math.round(Math.max(50, 80 - group.length*2 - diff*2)),
          rule: 'Grouped Settlement', status: 'SUGGESTED',
          scoreDetail: { composite: 0.65, scores: { amount: diff<=0.01?1:0.9, groupSize: group.length } },
          amountDiff: Math.round(diff*100)/100,
          dateDiff: Math.max(...group.map(c => daysBetween(suppTx.date, c.date))),
          companyTotal: Math.round(accum*100)/100,
          supplierTotal: sAmt,
        });
        mSupp.add(suppTx.id);
        group.forEach(c => mComp.add(c.id));
      }
    }
  }

  // ── Phase 4: Exception Classification ──
  const exceptions = [];
  companyTxns.filter(t => !mComp.has(t.id)).forEach(tx => {
    exceptions.push({
      transactionId: tx.id, ledgerSide: 'COMPANY',
      category: classifyException(tx, supplierTxns, mSupp, tolerances),
      transaction: tx,
    });
  });
  supplierTxns.filter(t => !mSupp.has(t.id)).forEach(tx => {
    exceptions.push({
      transactionId: tx.id, ledgerSide: 'SUPPLIER',
      category: classifyException(tx, companyTxns, mComp, tolerances),
      transaction: tx,
    });
  });

  // ── Unpack Grouped IDs ──
  matchGroups.forEach(g => {
    let cIds = [];
    g.companyItems.forEach(id => {
       const tx = companyTxns.find(x => x.id === id);
       if (tx) cIds.push(...tx._underlyingIds);
    });
    g.companyItems = Array.from(new Set(cIds));

    let sIds = [];
    g.supplierItems.forEach(id => {
       const tx = supplierTxns.find(x => x.id === id);
       if (tx) sIds.push(...tx._underlyingIds);
    });
    g.supplierItems = Array.from(new Set(sIds));
  });

  const matchedCompanyIds = new Set();
  const matchedSupplierIds = new Set();
  matchGroups.forEach(g => {
    g.companyItems.forEach(id => matchedCompanyIds.add(id));
    g.supplierItems.forEach(id => matchedSupplierIds.add(id));
  });

  // Store as Arrays so they survive JSON.stringify/parse (Sets serialize to {})
  const matchedCompanyArray = Array.from(matchedCompanyIds);
  const matchedSupplierArray = Array.from(matchedSupplierIds);

  return {
    matchGroups, exceptions,
    summary: {
      totalCompany: compTxnsRaw.length, totalSupplier: suppTxnsRaw.length,
      matched: matchGroups.filter(g => g.status === 'CONFIRMED').length,
      suggested: matchGroups.filter(g => g.status === 'SUGGESTED').length,
      unmatchedCompany: compTxnsRaw.length - matchedCompanyIds.size,
      unmatchedSupplier: suppTxnsRaw.length - matchedSupplierIds.size,
      autoMatchRate: compTxnsRaw.length > 0 ? Math.round((matchedCompanyIds.size/compTxnsRaw.length)*100) : 0,
    },
    // Store as arrays for JSON serialization, consumer should convert to Set if needed
    matchedCompanyIds: matchedCompanyArray,
    matchedSupplierIds: matchedSupplierArray,
  };
};

// ── Exception Classification ────────────
const classifyException = (tx, otherSide, matchedIds, tol) => {
  const amt = absAmount(tx);
  const other = otherSide.filter(o => !matchedIds.has(o.id));

  // Near-amount but different date = timing
  if (other.find(o => Math.abs(absAmount(o)-amt) <= tol.amountAbsolute && daysBetween(tx.date,o.date) > tol.dateDays)) return 'Timing Difference';
  // Same doc but amount mismatch
  if (other.find(o => docNoSimilarity(tx.docNo,o.docNo) > 0.7 && Math.abs(absAmount(o)-amt) > tol.amountAbsolute)) return 'Amount Mismatch';
  // Invoice ref found in other side
  if (tx.invoiceRef && other.find(o => (o.invoiceRef||'').includes(tx.invoiceRef) || (o.narration||'').toUpperCase().includes(tx.invoiceRef))) return 'Timing Difference';
  // Small amounts
  if (amt < tol.materialityThreshold) return 'Below Materiality';
  // Journal/round-off entries
  if (['Journal','TDS'].includes(tx.type) || /ROUND\s*OFF/i.test(tx.narration)) return 'Adjustment Entry';

  return 'Missing in Other Ledger';
};

// ── Duplicate Detection ─────────────────
export const detectDuplicates = (transactions) => {
  const dups = [];
  for (let i=0; i<transactions.length; i++) {
    for (let j=i+1; j<transactions.length; j++) {
      const a=transactions[i], b=transactions[j];
      if (Math.abs(absAmount(a)-absAmount(b)) <= 0.01 &&
          daysBetween(a.date,b.date) <= 3 &&
          (docNoSimilarity(a.docNo,b.docNo) > 0.8 || stringSimilarity(normalizeNarration(a.narration),normalizeNarration(b.narration)) > 0.7))
        dups.push({txA:a.id, txB:b.id});
    }
  }
  return dups;
};
